// Punch pair-streak milestones — client-side mirror of the crossing ladder the
// worker already awards XP against (V2_PUNCH_MILESTONES, cloudflare/src/scoring-v2.ts).
// This module is pure (no storage/network) so the crossing detector can be
// eyeballed/exercised directly: it takes the latest /punch/pairs rows plus the
// previously-persisted per-pair "floor" (see native/storage.ts key
// yelo.punchMilestone.v1, owned by the caller in App.tsx) and returns the one
// celebration to show, if any, plus the full re-baselined floor map to persist.
export const PUNCH_MILESTONES = [3, 7, 14, 30, 60] as const;
export type PunchMilestone = typeof PUNCH_MILESTONES[number];

export type MilestonePairState = "your_move" | "their_move" | "dying" | "dead";

export type MilestonePairInput = {
  // stable per-pair identity client-side. chain_id is NOT stable across a
  // rematch (a new chain gets a new id) but mate_handle is, so that's the key.
  pairKey: string;
  streak_days: number;
  state: MilestonePairState;
};

export type MilestoneCrossing = {
  pairKey: string;
  milestone: PunchMilestone;
  streak_days: number;
};

// The largest milestone at or below `days`, or 0 if none reached yet.
const floorOf = (days: number): number => {
  let floor = 0;
  for (const m of PUNCH_MILESTONES) {
    if (days >= m) floor = m;
    else break;
  }
  return floor;
};

// Pure crossing detector.
//
// Semantics:
// - A pair not yet present in `lastFloor` is a first sight: its floor is
//   recorded silently (no celebration) so shipping this feature never fires
//   retroactively for streaks that crossed a milestone before the OTA landed.
// - A pair whose floor increases versus its stored value, and whose current
//   `state` isn't "dead", is a crossing candidate.
// - A pair whose floor DECREASES (a rematch resets streak_days on a new
//   chain — see punch_pairs.streak_days in worker.ts) is re-baselined down,
//   not celebrated; this lets the new run re-earn the same milestones as it
//   climbs back up, which is the desired behaviour for a fresh streak.
// - If multiple pairs cross in the same update, only the single highest
//   milestone is celebrated; every other candidate is still folded into the
//   returned floor map (marked "seen") so it never queues up and fires later.
export const detectMilestoneCrossing = (
  pairs: MilestonePairInput[],
  lastFloor: Record<string, number>,
): { celebration: MilestoneCrossing | null; floors: Record<string, number> } => {
  const floors: Record<string, number> = { ...lastFloor };
  let celebration: MilestoneCrossing | null = null;
  for (const p of pairs) {
    const floor = floorOf(p.streak_days);
    const prev = floors[p.pairKey];
    const firstSight = prev === undefined;
    const crossed = !firstSight && floor > prev && p.state !== "dead";
    if (crossed && (!celebration || floor > celebration.milestone)) {
      celebration = { pairKey: p.pairKey, milestone: floor as PunchMilestone, streak_days: p.streak_days };
    }
    floors[p.pairKey] = floor;
  }
  return { celebration, floors };
};

// Milestone-appropriate escalation copy — headline size and tagline both
// ramp up the ladder; 60 gets the biggest, "legends" treatment.
export const MILESTONE_COPY: Record<PunchMilestone, { size: number; tagline: string }> = {
  3: { size: 26, tagline: "the streak is officially a thing." },
  7: { size: 28, tagline: "one full week. keep the mate on speed dial." },
  14: { size: 30, tagline: "two weeks straight — this is a habit now." },
  30: { size: 33, tagline: "a full month without missing a beat." },
  60: { size: 38, tagline: "SIXTY DAYS. legends." },
};
